import { Control, type ControlOptions } from "../core/Control.js";
import type { RenderContext } from "../render/RenderContext.js";
import type { Theme } from "../themes/Theme.js";

export interface SpinnerOptions extends ControlOptions {
  /** Diameter of the spinner. Defaults to the theme's `spinnerSize`. */
  size?: number;
  /** Seconds for one full turn. */
  spinSeconds?: number;
  /** Stop animating without hiding. A stopped spinner paints frozen. */
  spinning?: boolean;
}

/**
 * An indeterminate busy indicator.
 *
 * The theme owns the look through `drawSpinner`, so this control is a clock
 * and nothing more. It registers as an animator only while it is spinning and
 * actually on screen, so a spinner inside a hidden tab page costs no frames.
 *
 * Sizing is square: whichever of width and height is smaller wins, and the
 * result is centered. Set `size` to have the control size itself instead.
 */
export class Spinner extends Control {
  #spinning: boolean;
  #animating = false;

  spinSeconds: number;

  constructor(options: SpinnerOptions = {}) {
    super({ width: options.size ?? 28, height: options.size ?? 28, tabStop: false, ...options });
    this.spinSeconds = options.spinSeconds ?? 1.1;
    this.#spinning = options.spinning ?? true;
    if (options.size !== undefined) this.setSize(options.size, options.size);
    this.#syncAnimation();
  }

  get spinning(): boolean {
    return this.#spinning;
  }
  set spinning(v: boolean) {
    if (this.#spinning === v) return;
    this.#spinning = v;
    this.#syncAnimation();
    this.invalidate();
  }

  /** Phase around one turn, 0..1. */
  get phase(): number {
    if (this.spinSeconds <= 0) return 0;
    const now = this.desktop?.frameTime ?? 0;
    return (now % this.spinSeconds) / this.spinSeconds;
  }

  protected override onEnvironmentChanged(): void {
    // Gaining or losing a desktop changes where the animator registers.
    this.#animating = false;
    this.#syncAnimation();
  }

  #syncAnimation(): void {
    const desktop = this.desktop;
    if (!desktop) {
      this.#animating = false;
      return;
    }
    const want = this.#spinning && this.effectivelyVisible;
    if (want === this.#animating) return;
    this.#animating = want;
    if (want) desktop.addAnimator(this);
    else desktop.removeAnimator(this);
  }

  /** The largest centered square inside the control. */
  get spinnerRect(): { x: number; y: number; width: number; height: number } {
    const side = Math.min(this.width, this.height);
    return {
      x: Math.round((this.width - side) / 2),
      y: Math.round((this.height - side) / 2),
      width: side,
      height: side,
    };
  }

  protected override onPaint(rc: RenderContext, theme: Theme): void {
    this.#syncAnimation();
    theme.drawSpinner(rc, this.spinnerRect, {
      phase: this.#spinning ? this.phase : 0,
      disabled: !this.effectivelyEnabled,
    });
  }
}
